import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { EMPTY, Observable, of, Subject } from 'rxjs';
import { PageInfo } from '../../../_shared/models/adobe/page-info';
import { AdobeDtmService } from '../../../_shared/services/adobe-dtm.service';
import { Router } from '@angular/router';
import { CardService } from '../../services/card.service';
import { Card } from '../../../_shared/models/card-models/card';
import { environment } from '../../../../environments/environment';
import {
  ModalConfig,
  redemptionConfig
} from '../../../_shared/models/dashboard-models/dashboard-model';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AemService } from '../../../_shared/services/aem-content.service';
import { UserService } from '../../services/user.service';
import { AccountResponse } from '../../../_shared/models/account-models/account-response';
import { CardLookupRequest } from '../../../_shared/models/card-models/card-lookup-model';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DashboardComponent implements OnInit, OnDestroy {
  userName: string;
  cardNumber: number | string;
  cardType: Card;
  asOf: string = new Intl.DateTimeFormat('en-US', {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit'
  }).format(new Date());
  aemContent: any;
  cardTitle: any;
  cardImageUrl: string;
  totalEarnings: number;
  isLoadingDashboardData: boolean = false;
  isLoadingAemContent: boolean = false;
  modalConfigs: { [key: string]: ModalConfig } = {};
  redemptionOptions: redemptionConfig[] = [];
  showOfferSection: boolean = false;
  showNotificationBanner: boolean = false;

  modalVisible: boolean = false;
  currentModalConfig: ModalConfig | null = null;
  modalDesign: 'default' | 'alternate' = 'default';

  componentDestroyed = new Subject<void>();

  constructor(
    private readonly adobeDTMService: AdobeDtmService,
    private router: Router,
    private readonly cardService: CardService,
    private aem: AemService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.getContent();
    this.getCardDetails();
    this.sendTrackingInfo();
    this.setAnalytics();
  }

  async setAnalytics() {
    try {
      const satelite = await (window as any)._satellite;
      const digitalData = await (window as any).digitalData;
      if (
        typeof satelite !== 'undefined' &&
        typeof digitalData !== 'undefined'
      ) {
        satelite.track('page-load');
      }
    } catch (error) {}
  }

  getCardDetails() {
    this.isLoadingDashboardData = true;
    this.cardService
      .getCurrentCard()
      .pipe(
        takeUntil(this.componentDestroyed),
        switchMap((card: Card) => {
          if (!card) {
            this.isLoadingDashboardData = false;
            this.router.navigate(['**']);
            return [];
          }
          this.cardType = card;
          this.cardNumber = this.cardType?.last4CardNumber || 0;
          return this.cardService.getEarningsWithFallback(card.trackId);
        })
      )
      .subscribe(
        data => {
          if (data) {
            this.userName = data.customerDetails?.firstName;
            this.totalEarnings =
              data.earnings?.availableEarnings?.totalPointsConversion || 0;
          } else {
            this.totalEarnings = 0;
          }
          this.isLoadingDashboardData = false;
        },
        error => {
          this.totalEarnings = 0;
          this.isLoadingDashboardData = false;
        }
      );
  }

  getContent() {
    this.isLoadingAemContent = true;
    this.aem
      .getDashboardContent()
      .pipe(
        takeUntil(this.componentDestroyed),
        switchMap((content: any) => {
          if (Object.keys(content).length === 0) {
            return this.aem.fetchContent('dashboard');
          }
          return of(content);
        }),
        tap((content: any) => {
          if (!content) {
            this.isLoadingAemContent = false;
            return;
          }
          this.aemContent = content;
          this.cardImageUrl = `${environment.API_URL}/getAemImage?image=${content?.cardImage1}`;
          if (content.promotionalTiles) {
            this.modalConfigs = this.parsePromotionalTiles(
              content?.promotionalTiles
            );
          }
          this.updateRedemptionOptions(content);
          this.isLoadingAemContent = false;
        }),
        catchError(error => {
          this.aemContent = '';
          this.modalConfigs = {};
          this.redemptionOptions = [];
          this.isLoadingAemContent = false;
          return EMPTY; // Return an empty Observable
        })
      )
      .subscribe();
  }
  parsePromotionalTiles(jsonString: string): { [key: string]: ModalConfig } {
    const parsedTiles = JSON.parse(jsonString);
    const result: { [key: string]: ModalConfig } = {};

    Object.keys(parsedTiles).forEach(key => {
      const tile = parsedTiles[key][0];
      result[key] = {
        title: tile.title,
        content: tile.content,
        subContent: tile.subContent,
        primaryButtonText: tile.primaryButtonText,
        primaryAction: () => {
          if (tile.primaryAction) {
            window.open(tile.primaryAction, '_blank');
          }
        }
      };
    });
    return result;
  }
  updateRedemptionOptions(content: any): void {
    this.redemptionOptions = [
      {
        title: this.modalConfigs.purchaseOrLease?.title,
        image: `${environment.API_URL}/getAemImage?image=${content?.promotionImage1}`,
        modalType: 'purchaseOrLease'
      },
      {
        title: this.modalConfigs.eligibleAccessories?.title,
        image: `${environment.API_URL}/getAemImage?image=${content?.title8}`,
        modalType: 'eligibleAccessories'
      },
      {
        title: this.modalConfigs.paidCertifiedService?.title,
        image: `${environment.API_URL}/getAemImage?image=${content?.promotionImage3}`,
        modalType: 'paidCertifiedService'
      }
    ];
  }
  navigateToRedeemPoint() {
    this.router.navigate(['/redeem-points']);
  }
  openDealerLocator() {
    const url =
      'https://experience.gm.com/certified-service/locate-schedule-service?evar25=GMCard_Dashboard_Text_dealer';
    window.open(url, '_blank');
  }
  sendTrackingInfo(): void {
    let digitalData: PageInfo = {
      pageInfo: {
        cardType: this.cardType?.path,
        cardTypeOwned: this.cardType?.analyticsTypeOwned,
        loginType: this.cardType?.analyticsLoginType,
        offerID: '',
        pageGoal: 'nogoal',
        pageName: 'dashboard | logged | ' + this.cardType?.analyticsType,
        pageSubType: '',
        pageType: 'dashboard',
        seoStrategyPageName: 'Dashboard | GM Business Card',
        visitorStatus: 'logged'
      },
      formInfo: {
        formName: '',
        formFieldName: '',
        errorMessage: ''
      }
    };
    digitalData = this.adobeDTMService.setupWindowPageInfoData(
      digitalData,
      null,
      'Manage Card'
    );

    this.adobeDTMService.setWindowObjectData(digitalData);
    this.adobeDTMService.sendAdobeLaunchData();
  }
  openModal(configKey: string) {
    const config = this.modalConfigs[configKey];
    if (config) {
      this.currentModalConfig = { ...config };
      this.modalDesign = [
        'purchaseOrLease',
        'eligibleAccessories',
        'paidCertifiedService'
      ].includes(configKey)
        ? 'alternate'
        : 'default';
      this.modalVisible = true;
    }
  }
  executeModalAction(isPrimary: boolean) {
    if (this.currentModalConfig) {
      if (isPrimary) {
        this.currentModalConfig.primaryAction();
      }
    }
    this.closeModal();
  }
  closeModal() {
    this.modalVisible = false;
    this.currentModalConfig = null;
  }

  /**
   * Checks if notification banner should be displayed based on card lookup
   */
  checkNotificationBannerVisibility(): void {
    // Only proceed if we have valid card data
    if (!this.cardType?.last4CardNumber) {
      this.showNotificationBanner = false;
      return;
    }

    // Get user data to populate the request
    this.userService.user$
      .pipe(
        takeUntil(this.componentDestroyed),
        switchMap((user: AccountResponse) => {
          if (!user?.accountDetails?.cardHolderDetails) {
            this.showNotificationBanner = false;
            return of(false);
          }

          // Create card lookup request with current card and user information
          const request: CardLookupRequest = {
            last4CardNumber: this.cardType.last4CardNumber,
            postalCode:
              user.accountDetails.cardHolderDetails.address
                ?.addressPostalCode || '',
            lastName: user.accountDetails.cardHolderDetails.lastName || ''
          };

          // Call the card service to check notification banner visibility
          return this.cardService.checkNotificationBannerVisibility(request);
        }),
        catchError(error => {
          return of(false);
        })
      )
      .subscribe(shouldShow => {
        this.showNotificationBanner = shouldShow;
      });
  }
  ngOnDestroy(): void {
    this.componentDestroyed.next();
    this.componentDestroyed.unsubscribe();
  }
}
